import { useMutation, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { toastSuccessOptions } from "../../../../App";
import { handleGraphQLErrors } from "../../../../utils/handleGraphQLError";
import { ListLoading } from "../../../SharedComponents/ListLoading/ListLoading";
import { Modal } from "../../../SharedComponents/Modal/Modal";
import { Select, SelectOption } from "../../../SharedComponents/Select/Select";
import { AdminAccessControlActions } from "../../AdminUsersControlList/AdminAccessControlActions/AdminAccessControlActions";
import "./AdminCurrentAccountUsersList.css";

export const AdminCurrentAccountUsersList = ({
  users,
  accountId,
  loading,
  canEdit,
}) => {
  const { t } = useTranslation();
  const { data } = useQuery(getUserInformation);
  const [updateUserRole] = useMutation(updateSSOUserRoleMutation);
  const [revokeUser] = useMutation(removeUserMutation);

  const userId = data?.user?.id;

  const setRole = (id, value) => {
    updateUserRole({
      variables: { accountId, userId: id, role: value },
    }).catch((err) => handleGraphQLErrors(err));
  };

  const revokeUserHandler = (userId) => {
    return revokeUser({ variables: { userId, accountId } })
      .then(() => {
        toast(t("toasts.userAccessRevoked"), toastSuccessOptions);
      })
      .catch((err) => {
        handleGraphQLErrors(err);
      });
  };

  return (
    <div className="AdminCurrentAccountUsersList">
      <div className="AdminCurrentAccountUsersList__titlebar">
        <div className="AdminCurrentAccountUsersList__titlebar-name">
          {t("generic.name")}
        </div>
        <div className="AdminCurrentAccountUsersList__titlebar-role">
          {t("generic.role")}
        </div>
      </div>
      {loading && <ListLoading />}
      {users.map((usr) => {
        const userRole = usr.roles.sso.find((x) => x.accountId === accountId);
        return (
          <AdminCurrentAccountUsersListItem
            key={usr.id}
            id={usr.id}
            email={usr.email}
            role={userRole.role}
            setRole={setRole}
            status={usr.status}
            name={usr.name}
            userId={userId}
            canEdit={canEdit}
            revokeUser={revokeUserHandler}
          />
        );
      })}
    </div>
  );
};

const updateSSOUserRoleMutation = gql`
  mutation updateSSOUserRole(
    $accountId: String
    $userId: String
    $role: String
  ) {
    updateSSOUserRole(accountId: $accountId, userId: $userId, role: $role) {
      id
      email
      roles
      name
    }
  }
`;

const removeUserMutation = gql`
  mutation removeSSOUserFromAccount($userId: String, $accountId: String) {
    removeSSOUserFromAccount(userId: $userId, accountId: $accountId)
  }
`;

const AdminCurrentAccountUsersListItem = ({
  id,
  name = "-",
  email,
  role,
  setRole,
  actionType,
  accountId,
  status,
  userId,
  canEdit,
  revokeUser,
}) => {
  const { t } = useTranslation();
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const isDisabled = userId === id || !canEdit;
  return (
    <>
      {showRemoveUserModal && (
        <Modal
          accept={() => {
            revokeUser(id);
            setShowRemoveUserModal(false);
          }}
          reject={() => {
            setShowRemoveUserModal(false);
          }}
          title={t("users.revokeUserAccess")}
          acceptText={t("users.revoke")}
          rejectText={t("generic.cancel")}
          content={`${t("users.revokeAcceptance")} ${email}?`}
          acceptButtonColor="#AA2525"
        />
      )}
      <div className="AdminCurrentAccountUsersListItem">
        <div
          className="AdminUsersControlListItem__indicatior"
          style={{
            backgroundColor: status === "ACTIVE" ? "#56B267" : "#EAC75A",
          }}
        ></div>
        {status === "ACTIVE" && (
          <div className="AdminUsersControlListItem__name">{name}</div>
        )}
        {status === "PENDING" && (
          <div className="AdminUsersControlListItem__email">{email}</div>
        )}
        {status === "ACTIVE" && (
          <div className="AdminUsersControlListItem__email">
            &nbsp;({email})
          </div>
        )}
        {status === "PENDING" && (
          <div className="AdminUsersControlListItem__pending-invite">
            {t("users.invitePending")}
          </div>
        )}
        <div className="AdminUsersControlListItem__role">
          <Select
            change={(role) => setRole(id, role)}
            value={role}
            type="default"
            disabled={isDisabled}
          >
            {options.map(({ label, value }) => (
              <SelectOption key={value} value={value}>
                {t(label)}
              </SelectOption>
            ))}
          </Select>
        </div>
        <div className="AdminUsersControlListItem__action">
          <AdminAccessControlActions
            type={actionType}
            disabled={isDisabled}
            doAction={() => setShowRemoveUserModal(!showRemoveUserModal)}
          />
        </div>
      </div>
    </>
  );
};

const options = [
  {
    label: "roles.admin",
    value: "ADMIN",
  },
  {
    label: "roles.employee",
    value: "EMPLOYEE",
  },
  {
    label: "roles.supporter",
    value: "SUPPORTER",
  },
];

const getUserInformation = gql`
  query {
    user {
      id
      email
      name
    }
  }
`;
